// src/components/managed-sources/BqSourceSelector.tsx

import { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconPlus, IconRefresh, IconX } from "@tabler/icons-react";

import {
  listAuthorizations,
  listBigQueryProjects,
  listBigQueryDatasets,
  type BqProject,
  type BqDataset,
} from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import { useAuth } from "@/context/AuthContext";
import { BigQueryAuthorizationCreateModal } from "@/components/authorizations/BigQueryAuthorizationCreateModal";

type Props = {
  sourceProjectId: string;
  sourceDatasetId: string;
  onConfigChange: (projectId: string, datasetId: string) => void;
};

const CREATE_NEW_SENTINEL = "__create_new__";
const EMPTY_SENTINEL = "__empty__";

/**
 * Shared BQ source selector: picks a BigQuery authorization, then a GCP
 * project, then a dataset — all via runtime-SA impersonation.
 *
 * Used by:
 * - `GenericTransformAuthStep` (Facebook BYO, Google Ads BYO, and other
 *   generic BYO connectors — see `GENERIC_BYO_CONNECTORS` in
 *   `ManagedSourceWizard.tsx`)
 *
 * Emits `(projectId, datasetId)` via `onConfigChange` whenever either changes.
 * Resets project when auth changes; resets dataset when project changes.
 *
 * Project step, three shapes (see `BqProject` in `@/api/authorizations`):
 * - `is_fixed` — the authorization is bound to one project; shown read-only and
 *   selected for the user (the dataset step depends on `sourceProjectId`).
 * - `is_fallback` — project enumeration failed and the backend guessed; shown
 *   as a normal choice and NEVER auto-selected.
 * - neither — a real listing; a single entry is auto-selected.
 *
 * Inline-create flow: account admins see a "+ Create new BigQuery
 * authorization" sentinel option. Selecting it opens
 * `BigQueryAuthorizationCreateModal`. On success the auth list is refreshed
 * and the new auth is auto-selected; project list then loads via the
 * existing useEffect on `selectedAuthId`.
 */
export default function BqSourceSelector({
  sourceProjectId,
  sourceDatasetId,
  onConfigChange,
}: Props) {
  const { user } = useAuth();
  const isAccountAdmin =
    user?.activeAccount?.role === "OWNER" ||
    user?.activeAccount?.role === "ADMIN";

  const [auths, setAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);
  const [authsError, setAuthsError] = useState<string | null>(null);
  const [selectedAuthId, setSelectedAuthId] = useState<string | null>(null);

  const [projects, setProjects] = useState<BqProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  const [datasets, setDatasets] = useState<BqDataset[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [datasetsError, setDatasetsError] = useState<string | null>(null);
  // Bumped by the refresh button to re-run the dataset effect for the SAME
  // (auth, project). Granting the runtime SA access to a dataset is not
  // instantaneous — IAM takes minutes to propagate — and without this the only
  // way to re-check was a full page reload, which throws away the wizard.
  const [datasetsReloadKey, setDatasetsReloadKey] = useState(0);
  const loadedDatasetKeyRef = useRef<string | null>(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Load BigQuery authorizations
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingAuths(true);
      setAuthsError(null);
      try {
        const list = await listAuthorizations({ type: "BIGQUERY" });
        if (cancelled) return;
        setAuths(list);
        if (list.length === 1) {
          setSelectedAuthId(list[0].id);
        }
      } catch (e: any) {
        if (cancelled) return;
        // An empty dropdown with no explanation is an invisible failure: the
        // user reads "no authorizations exist" where the truth is "we couldn't
        // load them". Surface it, and log the cause (CLAUDE.md side-effect rule).
        console.error("Failed to load BigQuery authorizations", e);
        setAuths([]);
        setAuthsError(
          "We couldn't load your BigQuery authorizations. Refresh the page to try again.",
        );
      } finally {
        if (!cancelled) setLoadingAuths(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load projects when auth changes.
  // `cancelled` guards against out-of-order responses: switching authorizations
  // fires a second request while the first is in flight, and without this a slow
  // earlier response overwrites the newer list (and its auto-select would
  // configure a project from the *previous* authorization).
  useEffect(() => {
    if (!selectedAuthId) {
      setProjects([]);
      return;
    }
    let cancelled = false;
    async function loadProjects() {
      setLoadingProjects(true);
      setProjectsError(null);
      setProjects([]);
      try {
        const list = await listBigQueryProjects(selectedAuthId!);
        if (cancelled) return;
        setProjects(list);
        // Auto-select only when the single entry is trustworthy:
        // - `is_fixed`  → the authorization HAS this project; selecting it is
        //   the only correct outcome, and it is what makes the dataset effect
        //   below fire at all (it is guarded on `sourceProjectId`).
        // - `is_fallback` → enumeration failed and this is a guess; the user
        //   must choose deliberately. Same rule as ProfileConfigWizard.
        if (list.length === 1 && !list[0].is_fallback) {
          onConfigChange(list[0].project_id, "");
        }
      } catch (e: any) {
        if (cancelled) return;
        setProjectsError(e?.message || "Failed to load projects");
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    }
    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [selectedAuthId]);

  // Load datasets when project changes, or when the user asks for a re-check
  // (same out-of-order guard as above)
  useEffect(() => {
    if (!selectedAuthId || !sourceProjectId) {
      setDatasets([]);
      loadedDatasetKeyRef.current = null;
      return;
    }
    // A manual refresh re-queries the SAME project, so the options on screen are
    // still valid — clearing them there would blink out an already-picked
    // dataset for the duration of the request. Only a change of authorization or
    // project can leave a stale list behind, so only that path clears first.
    const targetKey = `${selectedAuthId}|${sourceProjectId}`;
    const isSameTarget = loadedDatasetKeyRef.current === targetKey;
    loadedDatasetKeyRef.current = targetKey;

    let cancelled = false;
    async function loadDatasets() {
      setLoadingDatasets(true);
      setDatasetsError(null);
      if (!isSameTarget) setDatasets([]);
      try {
        const list = await listBigQueryDatasets(selectedAuthId!, sourceProjectId);
        if (cancelled) return;
        setDatasets(list);
      } catch (e: any) {
        if (cancelled) return;
        setDatasetsError(e?.message || "Failed to load datasets");
      } finally {
        if (!cancelled) setLoadingDatasets(false);
      }
    }
    loadDatasets();
    return () => {
      cancelled = true;
    };
  }, [selectedAuthId, sourceProjectId, datasetsReloadKey]);

  function handleAuthSelectChange(value: string | null) {
    if (value === CREATE_NEW_SENTINEL) {
      // Defensive admin re-check (frontend role is advisory; backend is source of truth)
      if (!isAccountAdmin) return;
      setCreateModalOpen(true);
      return;
    }
    if (value === EMPTY_SENTINEL) return;
    handleAuthChange(value);
  }

  function handleAuthChange(authId: string | null) {
    setSelectedAuthId(authId);
    onConfigChange("", "");
    setProjects([]);
    setDatasets([]);
  }

  function handleProjectChange(projectId: string | null) {
    onConfigChange(projectId || "", "");
    setDatasets([]);
  }

  function handleDatasetChange(datasetId: string | null) {
    onConfigChange(sourceProjectId, datasetId || "");
  }

  // Fire-and-forget: the modal closes synchronously after calling onCreated.
  // Do NOT toggle loadingAuths during this reload — the parent must stay
  // mounted so React state updates land safely.
  async function handleAuthCreated(newAuth?: AuthorizationDTO) {
    // Reset downstream parent state FIRST so stale options don't flash
    onConfigChange("", "");
    setProjects([]);
    setDatasets([]);

    // Sequential reload-then-select avoids Mantine blank-flicker race
    try {
      const refreshed = await listAuthorizations({ type: "BIGQUERY" });
      setAuths(refreshed);
    } catch (e) {
      // Per CLAUDE.md side-effect-exception rule: log even when swallowed
      console.error(
        "Failed to refresh auth list after create — falling back to optimistic append",
        e,
      );
      if (newAuth) {
        setAuths((prev) =>
          prev.some((a) => a.id === newAuth.id) ? prev : [...prev, newAuth],
        );
      }
    }
    if (newAuth?.id && typeof newAuth.id === "string") {
      setSelectedAuthId(newAuth.id);
    }
  }

  // Build options: existing auths, plus EMPTY_SENTINEL when empty,
  // plus CREATE_NEW_SENTINEL only for admins.
  const authOptions: Array<{ value: string; label: string; disabled?: boolean }> = [];
  if (auths.length === 0) {
    authOptions.push({ value: EMPTY_SENTINEL, label: "—", disabled: true });
  } else {
    for (const a of auths) {
      authOptions.push({ value: a.id, label: a.name });
    }
  }
  if (isAccountAdmin) {
    authOptions.push({
      value: CREATE_NEW_SENTINEL,
      label: "+ Create new BigQuery authorization",
    });
  }

  const projectOptions = projects.map((p) => ({
    value: p.project_id,
    label: p.display_name || p.project_id,
  }));
  // The authorization is bound to one project by configuration — there is
  // nothing to pick, so we show a value instead of a (disabled) dropdown.
  const fixedProject =
    projects.length === 1 && projects[0].is_fixed ? projects[0] : null;
  // Enumeration failed; what we show is the service account's own project.
  const isFallbackList = projects.length > 0 && projects.some((p) => p.is_fallback);
  const datasetOptions = datasets.map((d) => ({
    value: d.dataset_id,
    label: d.location ? `${d.dataset_id} (${d.location})` : d.dataset_id,
  }));

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={40}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading authorizations...
        </Text>
      </Stack>
    );
  }

  return (
    <>
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={600} size="sm">
            Select source data
          </Text>

          <Select
            label="BigQuery authorization"
            placeholder="Select an authorization"
            data={authOptions}
            value={selectedAuthId}
            onChange={handleAuthSelectChange}
            size="sm"
            searchable={auths.length > 5}
            renderOption={({ option }) => {
              const opt = option as { value: string; label: string };
              if (opt.value === CREATE_NEW_SENTINEL) {
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      width: "100%",
                      borderTop: "1px solid var(--mantine-color-gray-3)",
                      marginTop: 4,
                      paddingTop: 8,
                      color: "var(--mantine-color-green-7)",
                      fontWeight: 500,
                    }}
                  >
                    <IconPlus size={14} />
                    <span>Create new BigQuery authorization</span>
                  </div>
                );
              }
              if (opt.value === EMPTY_SENTINEL) {
                return (
                  <span
                    style={{
                      color: "var(--mantine-color-gray-5)",
                      fontStyle: "italic",
                    }}
                  >
                    — no authorizations yet —
                  </span>
                );
              }
              return <span>{opt.label}</span>;
            }}
          />

          {authsError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {authsError}
            </Alert>
          )}

          {!isAccountAdmin && auths.length === 0 && !authsError && (
            <Alert color="yellow">
              No BigQuery authorizations yet. Ask an account admin to create one
              in <Text span fw={600}>Settings &rarr; Authorizations</Text>.
            </Alert>
          )}

          {selectedAuthId && fixedProject && (
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                GCP Project
              </Text>
              <Text size="sm">{fixedProject.project_id}</Text>
              <Text size="xs" c="dimmed">
                This project is fixed by the selected authorization.
              </Text>
            </Stack>
          )}

          {selectedAuthId && !fixedProject && (
            <Select
              label="GCP Project"
              placeholder={loadingProjects ? "Loading projects..." : "Select a project"}
              data={projectOptions}
              value={sourceProjectId || null}
              onChange={handleProjectChange}
              disabled={loadingProjects || projects.length === 0}
              size="sm"
              searchable={projects.length > 5}
              rightSection={loadingProjects ? <Loader size={14} /> : undefined}
            />
          )}

          {selectedAuthId && !fixedProject && isFallbackList && !loadingProjects && (
            <Text size="xs" c="dimmed">
              We couldn't list every project for this authorization, so only the
              service account's own project is shown. Pick it only if it is the
              one you meant.
            </Text>
          )}

          {projectsError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {projectsError}
            </Alert>
          )}

          {selectedAuthId && !loadingProjects && projects.length === 0 && !projectsError && (
            <Text size="sm" c="dimmed">
              This authorization has no accessible BigQuery projects.
            </Text>
          )}

          {sourceProjectId && (
            // The refresh control sits NEXT TO the Select, not in its
            // `rightSection`: with no datasets the Select is disabled, and a
            // disabled input's right section is not clickable — it would be
            // dead in the one state where the user needs it.
            <Group gap="xs" wrap="nowrap">
              <Select
                label="BigQuery Dataset"
                placeholder={loadingDatasets ? "Loading datasets..." : "Select a dataset"}
                data={datasetOptions}
                value={sourceDatasetId || null}
                onChange={handleDatasetChange}
                disabled={loadingDatasets || datasets.length === 0}
                size="sm"
                searchable={datasets.length > 5}
                rightSection={loadingDatasets ? <Loader size={14} /> : undefined}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setDatasetsReloadKey((k) => k + 1)}
                loading={loadingDatasets}
                aria-label="Refresh datasets"
                title="Refresh datasets"
                mt={28}
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Group>
          )}

          {datasetsError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {datasetsError}
            </Alert>
          )}

          {sourceProjectId && !loadingDatasets && datasets.length === 0 && !datasetsError && (
            <Text size="sm" c="dimmed">
              No datasets found in this project.
            </Text>
          )}

          {/*
            An empty list here is almost never "this project has no datasets" —
            it is "the grant on the dataset hasn't propagated yet". Say so, and
            point at the way to re-check. Shown for the failed call too: BigQuery
            answers a missing grant with a permission error.
          */}
          {sourceProjectId && !loadingDatasets && datasets.length === 0 && (
            <Text size="xs" c="dimmed">
              If you just granted the service account access, it can take a few
              minutes before your datasets appear. Use the refresh button to
              check again.
            </Text>
          )}
        </Stack>
      </Paper>

      <BigQueryAuthorizationCreateModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        backendUrl={import.meta.env.VITE_API_URL}
        isAccountAdmin={isAccountAdmin}
        onCreated={handleAuthCreated}
      />
    </>
  );
}
